import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { PlusCircle, Edit, Trash2, Eye, LogOut, FileText, TrendingUp, Settings, Target, Users, Sparkles, Send, Download, Mail, BarChart3, Zap, Shield, Link2, FileDown, FlaskConical, Linkedin } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, totalViews: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const blogsPerPage = 15;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBlogs();
  }, [page]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const skip = (page - 1) * blogsPerPage;
      const response = await fetch(`${BACKEND_URL}/api/blogs/?limit=${blogsPerPage}&skip=${skip}&status=all`);
      
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
        
        const published = data.blogs.filter(b => b.status === 'published').length;
        const draft = data.blogs.filter(b => b.status === 'draft').length;
        const totalViews = data.blogs.reduce((sum, b) => sum + (b.real_views || 0), 0);
        
        setStats({
          total: data.total || data.blogs.length,
          published: data.total || published,
          draft,
          totalViews
        });
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const handleDelete = async (blogId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blogs/${blogId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast({
          title: "Blog Deleted",
          description: "Blog has been successfully deleted",
        });
        fetchBlogs();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete blog",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                NSRM Admin
              </h1>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Blogs</CardDescription>
              <CardTitle className="text-3xl">{loading ? <span className="inline-block h-9 w-16 bg-gray-200 rounded animate-pulse" /> : stats.total}</CardTitle>
            </CardHeader>
            <CardContent>
              <FileText className="w-8 h-8 text-blue-600" />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Published</CardDescription>
              <CardTitle className="text-3xl text-green-600">{loading ? <span className="inline-block h-9 w-16 bg-gray-200 rounded animate-pulse" /> : stats.published}</CardTitle>
            </CardHeader>
            <CardContent>
              <Eye className="w-8 h-8 text-green-600" />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Drafts</CardDescription>
              <CardTitle className="text-3xl text-orange-600">{loading ? <span className="inline-block h-9 w-16 bg-gray-200 rounded animate-pulse" /> : stats.draft}</CardTitle>
            </CardHeader>
            <CardContent>
              <Edit className="w-8 h-8 text-orange-600" />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Views (Real)</CardDescription>
              <CardTitle className="text-3xl text-purple-600">{loading ? <span className="inline-block h-9 w-16 bg-gray-200 rounded animate-pulse" /> : stats.totalViews}</CardTitle>
            </CardHeader>
            <CardContent>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </CardContent>
          </Card>
        </div>

        {/* Create New Button */}
        <div className="mb-6 flex flex-wrap gap-4">
          <Button
            onClick={() => navigate('/admin/blog/new')}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            Create New Blog
          </Button>
          <Button
            onClick={() => navigate('/admin/quotes')}
            size="lg"
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            <Send className="w-5 h-5 mr-2" />
            Quotes
          </Button>
          <Button
            onClick={() => navigate('/admin/contracts')}
            size="lg"
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="contracts-btn"
          >
            <Shield className="w-5 h-5 mr-2" />
            Contracts & SLAs
          </Button>
          <Button
            onClick={() => navigate('/admin/internal-links')}
            size="lg"
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
            data-testid="internal-links-btn"
          >
            <Link2 className="w-5 h-5 mr-2" />
            Internal Links
          </Button>
          <Button
            onClick={() => navigate('/admin/lead-magnets')}
            size="lg"
            variant="outline"
            className="border-orange-600 text-orange-600 hover:bg-orange-50"
            data-testid="lead-magnets-btn"
          >
            <FileDown className="w-5 h-5 mr-2" />
            Lead Magnets
          </Button>
          <Button
            onClick={() => navigate('/admin/ab-testing')}
            size="lg"
            variant="outline"
            className="border-cyan-600 text-cyan-600 hover:bg-cyan-50"
            data-testid="ab-testing-btn"
          >
            <FlaskConical className="w-5 h-5 mr-2" />
            A/B Testing
          </Button>
          <Button
            onClick={() => navigate('/admin/linkedin')}
            size="lg"
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
            data-testid="linkedin-dashboard-btn"
          >
            <Linkedin className="w-5 h-5 mr-2" />
            LinkedIn Posts
          </Button>
          <Button
            onClick={() => navigate('/admin/subscribers')}
            size="lg"
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="subscribers-dashboard-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            Subscribers
          </Button>
          <Button
            onClick={() => navigate('/admin/retargeting')}
            size="lg"
            variant="outline"
            className="border-orange-600 text-orange-600 hover:bg-orange-50"
          >
            <Target className="w-5 h-5 mr-2" />
            Retargeting
          </Button>
          <Button
            onClick={() => navigate('/admin/leads')}
            size="lg"
            variant="outline"
            className="border-purple-600 text-purple-600 hover:bg-purple-50"
          >
            <Users className="w-5 h-5 mr-2" />
            Lead Pipeline
          </Button>
          <Button
            onClick={() => navigate('/admin/ai-blogs')}
            size="lg"
            variant="outline"
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            AI Blog Engine
          </Button>
          <Button
            onClick={() => navigate('/admin/content-engine')}
            size="lg"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
            data-testid="content-engine-btn"
          >
            <Zap className="w-5 h-5 mr-2" />
            Content Engine
          </Button>
          <Button
            onClick={() => navigate('/admin/blog-analytics')}
            size="lg"
            variant="outline"
            className="border-purple-600 text-purple-600 hover:bg-purple-50"
            data-testid="blog-analytics-btn"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Blog Analytics
          </Button>
          <Button
            onClick={() => navigate('/admin/email-settings')}
            size="lg"
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Mail className="w-5 h-5 mr-2" />
            Email Settings
          </Button>
          <Button
            onClick={() => {
              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
              window.open(`${BACKEND_URL}/api/admin/export/leads/csv`, '_blank');
            }}
            size="lg"
            variant="outline"
            className="border-gray-600 text-gray-600 hover:bg-gray-50"
            data-testid="export-leads-csv-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Leads CSV
          </Button>
        </div>

        {/* Blogs List */}
        <Card>
          <CardHeader>
            <CardTitle>All Blogs</CardTitle>
            <CardDescription>Manage your blog posts {!loading && `(${stats.total} total)`}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={`skeleton-${i}`} className="animate-pulse flex items-center justify-between p-4 border border-gray-100 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-100 rounded w-full" />
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                    </div>
                    <div className="flex gap-2">
                      <div className="h-8 w-8 bg-gray-200 rounded" />
                      <div className="h-8 w-8 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : blogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No blogs yet. Create your first one!
              </div>
            ) : (
              <div className="space-y-4">
                {blogs.map((blog) => (
                  <div
                    key={blog.blogId}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{blog.title}</h3>
                        <Badge variant={blog.status === 'published' ? 'default' : 'secondary'}>
                          {blog.status}
                        </Badge>
                        <Badge variant="outline">{blog.category}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{blog.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>By {blog.author}</span>
                        <span>-</span>
                        <span>{formatDate(blog.publishedAt)}</span>
                        <span>-</span>
                        <span>{blog.real_views || 0} views</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/blog/edit/${blog.blogId}`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(blog.blogId, blog.title)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {stats.total > blogsPerPage && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-gray-500">
                      Page {page} of {Math.ceil(stats.total / blogsPerPage)} ({stats.total} blogs)
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={page >= Math.ceil(stats.total / blogsPerPage)}
                        onClick={() => setPage(p => p + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
};